import { orbitalWatchAccountUrl, orbitalWatchApps, orbitalWatchEnvironment } from '@orbitalwatch/design';
import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRenderMode } from '../../astromark';
import { AppShell, Icons, SpaceChip } from '../../ds';
import type { AppShellProps, RailEntry } from '../../ds';
import { useProposedCount } from '../../features/changeRequests/hooks/useProposedCount';
import { ConfigurationProvider } from '../../lib/infrastructure/configuration/ConfigurationProvider';
import { useIdentityUserDisplayName } from '../../lib/infrastructure/identity/hooks/useIdentityUserDisplayName';
import { useSelectedSpace } from '../../lib/infrastructure/ui/UIServices';
import { RenderModeChip } from './RenderModeControls';
import { renderModeMenuItems } from './renderModeMenu';
import { railModel, spaceIdOf, topBarEnv } from './shellModel';
import type { RailModelEntry } from './shellModel';
import { useTheme } from './theme';
import type { ThemeChoice } from './theme';

const SIGN_OUT_PATH = '/identity/logout';

const RAIL_ICONS: Record<RailModelEntry['key'], ReactNode> = {
    spaces: <Icons.book size={20} />,
    overview: <Icons.eye size={20} />,
    pages: <Icons.pages size={20} />,
    changes: <Icons.pullRequest size={20} />,
    settings: <Icons.settings size={20} />,
};

const THEME_ITEMS: { choice: ThemeChoice; label: string }[] = [
    { choice: 'dark', label: 'Theme: Dark' },
    { choice: 'light', label: 'Theme: Light' },
    { choice: 'system', label: 'Theme: System' },
];

/**
 * Ark's frame around every signed-in route (`ds/AppShell` over the library shell): brand "Ark" → /spaces, the env pill,
 * the open space as a chip in the context slot (then the renderer override chip while one is set), the product switcher,
 * the account menu (View account, Theme, Renderer, Sign out) and the rail of `railModel`. Screens add the sub-nav column with `ShellSubNav` (the page tree).
 */
export function ArkShell(props: { children: ReactNode }) {
    const config = ConfigurationProvider.getConfiguration();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const spaceId = spaceIdOf(pathname);
    const selectedSpace = useSelectedSpace();
    const proposedCount = useProposedCount(spaceId);
    const { userDisplayName, userDisplayEmail } = useIdentityUserDisplayName();
    const [theme, setTheme] = useTheme();
    const [renderMode, setRenderMode] = useRenderMode();
    const suiteEnv = orbitalWatchEnvironment(config.App?.BackendUrl);

    const rail = useMemo((): RailEntry[] => railModel(pathname, proposedCount).map(entry => ({
        label: entry.label,
        icon: RAIL_ICONS[entry.key],
        to: entry.to,
        active: entry.active,
        disabled: entry.disabled,
        badge: entry.badge,
        bottom: entry.bottom,
        'aria-label': entry['aria-label'],
    })), [pathname, proposedCount]);

    // plain objects: rebuilt per render (cheap), so nothing stale is memoised
    const products: AppShellProps['products'] = {
        label: 'Switch product',
        header: 'Products',
        items: orbitalWatchApps(suiteEnv).map(app => {
            const isArk = app.label === 'Ark';
            return {
                label: app.label,
                icon: isArk ? <Icons.ark /> : <Icons.external />,
                onClick: isArk ? () => navigate('/spaces') : () => window.open(app.href, '_blank', 'noopener,noreferrer'),
            };
        }),
    };

    const name = userDisplayName ?? userDisplayEmail ?? 'Signed in';
    const account: AppShellProps['account'] = {
        name,
        email: userDisplayEmail,
        items: [
            { label: 'View account', icon: <Icons.account />, onClick: () => window.open(orbitalWatchAccountUrl(suiteEnv), '_blank', 'noopener,noreferrer') },
            ...THEME_ITEMS.map((item, index) => ({
                label: item.label,
                // The current choice carries the check; the others keep its width so the labels line up.
                icon: item.choice === theme ? <Icons.check /> : <span className="ark-menu-icon-spacer" />,
                onClick: () => setTheme(item.choice),
                separatorBefore: index === 0,
            })),
            ...renderModeMenuItems(renderMode, setRenderMode),
            { label: 'Sign out', icon: <Icons.signOut />, onClick: () => navigate(SIGN_OUT_PATH), separatorBefore: true },
        ],
    };

    // the chip appears once the space module has loaded the space (same id as the route)
    const spaceName = spaceId && selectedSpace?.id === spaceId ? selectedSpace.name : undefined;
    const spaceChip = spaceId && spaceName ? <SpaceChip name={spaceName} to={`/spaces/${encodeURIComponent(spaceId)}/pages`} /> : null;
    const overrideChip = renderMode !== 'auto' ? <RenderModeChip mode={renderMode} onReset={() => setRenderMode('auto')} /> : null;
    const context = spaceChip || overrideChip ? <>{spaceChip}{overrideChip}</> : undefined;

    return (
        <AppShell
            appName="Ark"
            homeTo="/spaces"
            env={topBarEnv(config.Environment)}
            context={context}
            railLabel={spaceId ? 'Space sections' : 'Ark sections'}
            rail={rail}
            products={products}
            account={account}
        >
            {props.children}
        </AppShell>
    );
}
